import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase.js';
import Sidebar from '../shared/Sidebar';
import TemplateEditorModal from '../shared/templates/TemplateEditorModal';
import { templatesApi } from '../../utils/templatesApi';
import '../../css/DocumentationMainPage.css';
import '../../css/DocumentationHomeComponent.css';
import '../shared/templates/TemplateGallery.css';

const API_BASE = 'https://bluebirddocumentationadmin.pythonanywhere.com';

/**
 * /templates — My Templates (BBD-109): manage personal templates and hide
 * or show Bluebird presets.
 */
const MyTemplates = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState('mine');
    const [templates, setTemplates] = useState([]);
    const [presets, setPresets] = useState([]);
    const [hidden, setHidden] = useState([]);
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editor, setEditor] = useState({ open: false, template: null });
    const [busyId, setBusyId] = useState(null);
    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
        const unsub = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
            else setAuthReady(true);
        });
        return () => unsub();
    }, [navigate]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [mine, pre] = await Promise.all([
                templatesApi.list({ includeContent: false }),
                templatesApi.listPresets({ all: true, includeContent: false }),
            ]);
            setTemplates(mine.templates || []);
            setPresets(pre.presets || []);
            setHidden(pre.hidden || []);
        } catch (e) {
            setError(e.message || 'Could not load templates');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!authReady) return;
        load();
        (async () => {
            try {
                const user = auth.currentUser;
                const token = await user.getIdToken();
                const res = await fetch(`${API_BASE}/workspaces/`, {
                    headers: { Authorization: token, 'auth-Token': `Bearer ${token}`, 'Requesting-User-Uid': user.uid },
                });
                if (res.ok) {
                    const data = await res.json();
                    setWorkspaces((Array.isArray(data) ? data : []).filter((w) => ['OWNER', 'ADMIN', 'EDITOR'].includes(w.role)));
                }
            } catch (e) {
                /* copy-to-workspace simply stays unavailable */
            }
        })();
    }, [authReady, load]);

    const withBusy = async (id, fn) => {
        setBusyId(id);
        setError('');
        try {
            await fn();
        } catch (e) {
            setError(e.message || 'Something went wrong');
        } finally {
            setBusyId(null);
        }
    };

    const handleDuplicate = (t) => withBusy(t.id, async () => {
        await templatesApi.duplicate(t.id);
        await load();
    });

    const handleDelete = (t) => {
        if (!window.confirm(`Delete the template "${t.name}"? Documents already created from it are not affected.`)) return;
        withBusy(t.id, async () => {
            await templatesApi.remove(t.id);
            await load();
        });
    };

    const handleCopyToWorkspace = (t) => {
        if (!workspaces.length) return;
        const list = workspaces.map((w, i) => `${i + 1}. ${w.name}`).join('\n');
        const answer = window.prompt(`Copy "${t.name}" to which workspace?\n${list}\n\nEnter a number:`);
        const idx = parseInt(answer, 10) - 1;
        if (Number.isNaN(idx) || !workspaces[idx]) return;
        withBusy(t.id, async () => {
            const copy = await templatesApi.copy(t.id, { workspaceId: workspaces[idx].id });
            alert(`Copied as "${copy.name}" to ${workspaces[idx].name}.`);
        });
    };

    const togglePreset = (slug) => withBusy(slug, async () => {
        const res = hidden.includes(slug) ? await templatesApi.unhidePreset(slug) : await templatesApi.hidePreset(slug);
        setHidden(res.hidden || []);
    });

    const kindLabel = { page: 'Page', space: 'Space', kit: 'Kit', snippet: 'Snippet' };

    return (
        <div className="DocumentationMainPage__div">
            <Sidebar />
            <div className="DocumentationMainPage__main-content">
                <div className="MyTemplates__main">
                    <div className="DocumentationHomeComponent__dashboard-header MyTemplates__header">
                        <h1 className="DocumentationHomeComponent__dashboard-h1">Templates</h1>
                        <button type="button" className="TplStart__btn TplStart__btn--primary" onClick={() => setEditor({ open: true, template: null })}>
                            <i className="fas fa-plus"></i>&nbsp; New template
                        </button>
                    </div>
                    <div className="MyTemplates__tabs" role="tablist">
                        <button type="button" role="tab" aria-selected={tab === 'mine'} className={`MyTemplates__tab${tab === 'mine' ? ' MyTemplates__tab--active' : ''}`} onClick={() => setTab('mine')}>
                            My templates ({templates.length})
                        </button>
                        <button type="button" role="tab" aria-selected={tab === 'presets'} className={`MyTemplates__tab${tab === 'presets' ? ' MyTemplates__tab--active' : ''}`} onClick={() => setTab('presets')}>
                            Bluebird presets ({presets.length})
                        </button>
                    </div>
                    {error && <div className="TplGallery__error" style={{ padding: '0 0 10px' }}>{error}</div>}
                    {loading && <div className="TplGallery__status">Loading…</div>}

                    {!loading && tab === 'mine' && (
                        templates.length === 0 ? (
                            <div className="MyTemplates__empty">
                                No templates yet. Create one here, or open any document and choose <strong>Save as template</strong>.
                            </div>
                        ) : (
                            <div className="MyTemplates__grid">
                                {templates.map((t) => (
                                    <div className="MyTemplates__card" key={t.id}>
                                        <div className="MyTemplates__cardHead">
                                            <div>
                                                <div className="MyTemplates__cardName">
                                                    {t.name}
                                                    <span className="TplGallery__badge">{kindLabel[t.kind] || t.kind}</span>
                                                </div>
                                                <div className="MyTemplates__cardDesc">{t.description || <em>No description yet</em>}</div>
                                            </div>
                                        </div>
                                        <div className="MyTemplates__cardMeta">
                                            {t.category ? `${t.category} · ` : ''}used {t.usage_count} {t.usage_count === 1 ? 'time' : 'times'}
                                            {t.fields?.length ? ` · ${t.fields.length} ${t.fields.length === 1 ? 'field' : 'fields'}` : ''}
                                        </div>
                                        <div className="MyTemplates__actions">
                                            <button type="button" className="TplGallery__btn" disabled={busyId === t.id} onClick={() => setEditor({ open: true, template: t })}>Edit</button>
                                            <button type="button" className="TplGallery__btn" disabled={busyId === t.id} onClick={() => handleDuplicate(t)}>Duplicate</button>
                                            {workspaces.length > 0 && (
                                                <button type="button" className="TplGallery__btn" disabled={busyId === t.id} onClick={() => handleCopyToWorkspace(t)}>Copy to workspace</button>
                                            )}
                                            {t.kind !== 'snippet' && (
                                                <button type="button" className="TplGallery__btn" onClick={() => navigate(`/create?template=${encodeURIComponent(t.ref)}`)}>Use</button>
                                            )}
                                            <button type="button" className="TplGallery__btn" disabled={busyId === t.id} onClick={() => handleDelete(t)} style={{ color: 'var(--bb-danger, #dc3545)' }}>Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {!loading && tab === 'presets' && (
                        <>
                            <p className="TplStart__note">Hidden presets stay out of the gallery. You can show them again here at any time.</p>
                            <div className="MyTemplates__grid">
                                {presets.map((p) => {
                                    const isHidden = hidden.includes(p.slug);
                                    return (
                                        <div className={`MyTemplates__card${isHidden ? ' MyTemplates__card--hidden' : ''}`} key={p.slug}>
                                            <div className="MyTemplates__cardHead">
                                                <div>
                                                    <div className="MyTemplates__cardName">
                                                        {p.name}
                                                        <span className="TplGallery__badge">{kindLabel[p.kind] || p.kind}</span>
                                                        {p.recording && <span className="TplGallery__badge">Recording</span>}
                                                    </div>
                                                    <div className="MyTemplates__cardDesc">{p.description}</div>
                                                </div>
                                            </div>
                                            <div className="MyTemplates__cardMeta">{p.category}</div>
                                            <div className="MyTemplates__actions">
                                                <button type="button" className="TplGallery__btn" disabled={busyId === p.slug} onClick={() => togglePreset(p.slug)}>
                                                    {isHidden ? 'Show in gallery' : 'Hide from gallery'}
                                                </button>
                                                {p.kind !== 'snippet' && !p.recording && (
                                                    <button type="button" className="TplGallery__btn" onClick={() => navigate(`/create?template=${encodeURIComponent(p.ref)}`)}>Use</button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <TemplateEditorModal
                isOpen={editor.open}
                template={editor.template}
                onClose={() => setEditor({ open: false, template: null })}
                onSaved={() => load()}
            />
        </div>
    );
};

export default MyTemplates;
